<template>
    <div class="tile-content" v-if="hasMpi">
        <div class="tile-subtitle">
            <h3>3D Secure</h3>
        </div>
        <p class="stack-m">To allow 3DS transactions through this gateway account, you need to select an MPI. The list
            of MPIs varies based on what is currently integrated in Rebilly. If an MPI isn't available, select <code>Other</code>
            MPI if you are already hosting a 3DS integration with your preferred MPI; you can POST the results to our
            <code>/3dsecure</code> API endpoint and we will attempt to send the key values along with the sale
            transaction.</p>
        <div class="grid-content">
            <div class="grid-item grid-item-half grid-item-break-half">
                <r-checkbox
                    label="Enable 3D Secure"
                    :value="true"
                    :selected="threeDSecure"
                    @input="update3DS"
                />
            </div>
            <div class="grid-item grid-item-half grid-item-break-half" v-if="threeDSecure">
                <v-select
                    label="MPI"
                    :options="mpiList"
                    :value="mpiName"
                    @input="updateMpiSelection"
                />
            </div>
            <div
                v-if="threeDSecure && hasMpiDefinition"
                class="grid-item grid-item-full">
                <dynamic-field-configuration
                    ref="fieldConfig"
                    :fields="mpiFields"
                    :config="mpiConfig"
                    :is-updating="isUpdating"
                    @input="updateFields"/>
            </div>
        </div>
    </div>
</template>

<script>
    import {schema} from '@/common/spec/spec-cache';
    import {
        vErrorMessages, vSelect,
    } from '@/components/form';
    import validationMixins from '../validation-mixins';
    import DynamicFieldConfiguration from '../dynamic-field-configuration';

    export default {
        name: 'three-d-secure',
        mixins: [validationMixins],
        components: {
            vErrorMessages,
            vSelect,
            DynamicFieldConfiguration,
        },
        props: {
            gatewayName: {
                type: String,
            },
            threeDSecure: {
                type: Boolean,
            },
            mpi: {
                type: Object,
            },
            isUpdating: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            hasMpi() {
                return this.definition !== undefined
                    && !!this.definition.properties
                    && !!this.definition.properties.mpi;
            },
            definition() {
                return schema[this.gatewayName].allOf.find(def => !!def.type);
            },
            mpiList() {
                const [entity] = this.definition.properties.mpi.$ref.split('/').reverse();
                return [...schema[entity].properties.name.enum];
            },
            mpiName() {
                if (this.mpi && this.mpi.name) {
                    // if the name isn't null use it
                    return this.mpi.name;
                }
                // otherwise select the first MPI available
                const [firstItem] = this.mpiList;
                return firstItem;
            },
            hasMpiDefinition() {
                return this.mpiName in schema
                    && schema[this.mpiName].allOf.find(ref => !!ref.type) !== undefined;
            },
            mpiFields() {
                const config = schema[this.mpiName].allOf.find(ref => !!ref.type);
                return config || [];
            },
        },
        data() {
            return {
                mpiConfig: this.mpi || {},
            };
        },
        methods: {
            update3DS(value) {
                let mpi = value ? {...this.mpi} : null;
                // when toggled on, set a default MPI to the parent,
                // otherwise the value will be null if the user doesn't update
                if (value === true && this.mpi === null) {
                    mpi = {name: this.mpiName};
                }
                this.$emit('input', {
                    threeDSecure: value,
                    mpi,
                });
            },
            updateMpiSelection(mpi) {
                this.$emit('input', {
                    threeDSecure: this.threeDSecure,
                    mpi: {name: mpi},
                });
            },
            updateFields(mpiFields) {
                this.$emit('input', {
                    threeDSecure: this.threeDSecure,
                    mpi: {
                        ...mpiFields,
                        name: this.mpiName,
                    },
                });
            },
            getValidationAggregatorDefinition() {
                return {
                    name: 'three-d-secure',
                    rules: [
                        {
                            name: '3D Secure Configuration is invalid',
                            delegate: () => {
                                if (this.threeDSecure && this.hasMpi && this.hasMpiDefinition) {
                                    return this.$refs.fieldConfig.validate();
                                }
                                return false;
                            },
                        },
                    ],
                };
            },
        },
    };
</script>
